import { MicIcon, Square } from "lucide-react";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import spaceInfo from "~/assets/spaceInfo.svg";
import spaceInfoStop from "~/assets/spaceInfoStop.svg";
import { LoaderCircle } from "~/components/animate-ui/icons/loader-circle";
import { Button } from "~/components/ui/button";
import { toast } from "~/hooks/use-toast";
import { cn } from "~/lib/utils";
import {
  SpeechRecognition,
  SpeechRecognitionErrorEvent,
  SpeechRecognitionEvent,
} from "~/types/speechRecognition";
import { trackEvent } from "~/utils/analytics";
import { AudioVisualizer } from "./AudioVisualizer";

interface AudioRecordingButtonProps {
  className?: string;
  onRecordingStateChange?: (isRecording: boolean) => void;
  isProcessingImprovement: boolean;
  isLoading?: boolean;
  onAutoImproveChunk: (rawTranscriptChunk: string) => void;
  onRecordingStop: () => Promise<void>;
  isProcessingQueue: boolean;
  onAudioUpload: (text: string) => void;
  onTextSpeechUpdate: (text: string) => void;
}

export interface AudioRecordingButtonHandle {
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<void>;
  toggleRecording: () => Promise<void>;
  isRecording: () => boolean;
}

export const AudioRecordingButton = forwardRef<
  AudioRecordingButtonHandle,
  AudioRecordingButtonProps
>(function AudioRecordingButton(
  {
    className,
    isProcessingImprovement,
    isLoading,
    onRecordingStateChange,
    onAutoImproveChunk,
    onRecordingStop,
    // onAudioUpload,
    isProcessingQueue,
    onTextSpeechUpdate,
  },
  ref
) {
  const [isRecording, setIsRecording] = useState(false);
  const [, setVolume] = useState<number>(0);
  const [audioStream, setAudioStream] = useState<MediaStream | null>(null);
  const audioStreamRef = useRef<MediaStream | null>(null);
  const isSpaceDownRef = useRef<boolean>(false);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const transcriptRef = useRef<string>("");
  const interimTranscriptRef = useRef<string>("");
  const isRecordingRef = useRef<boolean>(false);
  const autoSaveIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastAutoSaveTimeRef = useRef<number>(0);
  const speechTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasFlushedOnStopRef = useRef<boolean>(false);

  useEffect(() => {
    onRecordingStateChange?.(isRecording);
    isRecordingRef.current = isRecording;
  }, [isRecording, onRecordingStateChange]);

  useEffect(() => {
    return () => {
      // Clean up recognition
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
          recognitionRef.current = null;
        } catch (error) {}
      }

      // Stop audio stream (visualizer)
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach((t) => t.stop());
        audioStreamRef.current = null;
      }
      setAudioStream(null);

      // Clean up auto-save interval
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }

      // Clean up speech timeout
      if (speechTimeoutRef.current) {
        clearTimeout(speechTimeoutRef.current);
        speechTimeoutRef.current = null;
      }

      setIsRecording(false);
      setVolume(0);
    };
  }, []);

  // Spacebar shortcut: hold to record, release to stop
  const isEditableTarget = useCallback((target: EventTarget | null) => {
    const el = target as HTMLElement | null;
    if (!el) return false;
    const tag = el.tagName?.toLowerCase();
    if (tag === "input" || tag === "textarea") return true;
    if ((el as any).isContentEditable) return true;
    return !!el.closest?.('[contenteditable="true"]');
  }, []);

  const showToastMessage = useCallback((title: string, message: string) => {
    toast({
      variant: "default",
      title: title,
      description: message,
    });
  }, []);

  // Flush both final and interim text immediately (used when stopping)
  const flushAllTranscriptNow = useCallback(() => {
    const combined =
      `${transcriptRef.current} ${interimTranscriptRef.current}`.trim();
    if (combined) {
      onAutoImproveChunk(combined);
      transcriptRef.current = "";
      interimTranscriptRef.current = "";
      lastAutoSaveTimeRef.current = Date.now();
    }
  }, [onAutoImproveChunk]);

  const startAutoSave = useCallback(() => {
    if (autoSaveIntervalRef.current) {
      clearInterval(autoSaveIntervalRef.current);
    }
    autoSaveIntervalRef.current = setInterval(() => {
      // Send every 5 seconds if user is still recording and has content
      if (isRecordingRef.current) {
        const finalText = transcriptRef.current.trim();

        if (finalText) {
          onAutoImproveChunk(finalText);
          transcriptRef.current = "";
          lastAutoSaveTimeRef.current = Date.now();
        }
      }
    }, 5000);
  }, [onAutoImproveChunk]);

  const stopAutoSave = useCallback(() => {
    if (autoSaveIntervalRef.current) {
      clearInterval(autoSaveIntervalRef.current);
      autoSaveIntervalRef.current = null;
    }

    if (speechTimeoutRef.current) {
      clearTimeout(speechTimeoutRef.current);
      speechTimeoutRef.current = null;
    }
  }, []);

  const requestMicrophoneStream =
    useCallback(async (): Promise<MediaStream | null> => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        return stream;
      } catch (error) {
        if (error instanceof Error) {
          if (error.name === "NotAllowedError") {
            showToastMessage(
              "Permission Denied",
              "Please allow microphone access to use voice recording."
            );
          } else if (error.name === "NotFoundError") {
            showToastMessage(
              "No Microphone",
              "No microphone found. Please connect a microphone and try again."
            );
          } else {
            showToastMessage(
              "Microphone Error",
              "Unable to access microphone. Please check your settings."
            );
          }
        }
        return null;
      }
    }, [showToastMessage]);

  const initializeSpeechRecognition = useCallback(() => {
    const SpeechRecognition =
      (window as any).SpeechRecognition ||
      (window as any).webkitSpeechRecognition;

    if (!SpeechRecognition) {
      showToastMessage(
        "Error",
        "Speech recognition is not supported in this browser. Please use Chrome, Safari, or Edge."
      );
      return null;
    }

    const recognition: SpeechRecognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      const newVolume = Math.min(0.5 + Math.random() * 0.7, 1);
      setVolume(newVolume);
      let interimTranscript = "";
      let finalTranscript = "";

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript + " ";
        } else {
          interimTranscript += transcript;
        }
      }

      if (finalTranscript) {
        transcriptRef.current += finalTranscript;
      }

      interimTranscriptRef.current = interimTranscript;
      onTextSpeechUpdate(
        transcriptRef.current.length
          ? transcriptRef.current
          : interimTranscriptRef.current
      );

      setTimeout(() => {
        setVolume(0);
      }, 200);
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      if (isRecording) {
        setIsRecording(false);
      }

      if (event.error === "no-speech") return;

      // eslint-disable-next-line no-console
      console.error("Speech recognition error:", event.error, event.message);

      const messages: Record<string, string> = {
        "not-allowed": "Please allow microphone access and try again.",
        network: "Network error occurred. Please check your connection.",
      };

      showToastMessage(
        "Speech Recognition Error",
        messages[event.error] ?? "An unexpected error occurred."
      );
    };

    recognition.onend = () => {
      if (isRecordingRef.current) {
        setTimeout(() => {
          if (recognitionRef.current && isRecordingRef.current) {
            recognitionRef.current.start();
          }
        }, 100);
      } else {
        stopAutoSave();
        // Ensure we send the very last interim + final chunk on stop (only once)
        if (!hasFlushedOnStopRef.current) {
          flushAllTranscriptNow();
          hasFlushedOnStopRef.current = true;
        }
        onRecordingStop();
        // Ensure audio stream is stopped when recording fully ends
        if (audioStreamRef.current) {
          audioStreamRef.current.getTracks().forEach((t) => t.stop());
          audioStreamRef.current = null;
          setAudioStream(null);
        }
      }
    };

    return recognition;
  }, [
    showToastMessage,
    isRecording,
    stopAutoSave,
    onRecordingStop,
    flushAllTranscriptNow,
  ]);

  const handleRecordingClick = useCallback(async () => {
    trackEvent({ eventName: "app/notes/record_audio.button/click" });

    if (isRecording) {
      setIsRecording(false);
      isRecordingRef.current = false;

      stopAutoSave();

      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (error) {}
      }
      setVolume(0);
      // Stop audio stream for visualizer
      if (audioStream) {
        audioStream.getTracks().forEach((t) => t.stop());
        setAudioStream(null);
      }
    } else {
      const stream = await requestMicrophoneStream();
      if (!stream) return;
      setAudioStream(stream);
      audioStreamRef.current = stream;

      const recognition = initializeSpeechRecognition();
      if (recognition) {
        recognitionRef.current = recognition;
        setIsRecording(true);
        isRecordingRef.current = true;
        hasFlushedOnStopRef.current = false;
        lastAutoSaveTimeRef.current = Date.now();
        try {
          recognition.start();
        } catch (error) {
          showToastMessage(
            "Speech Recognition Error",
            "Failed to start speech recognition. Please try again."
          );
          setIsRecording(false);
          isRecordingRef.current = false;
          // Stop and release stream on failure
          stream.getTracks().forEach((t) => t.stop());
          setAudioStream(null);
          audioStreamRef.current = null;
          return;
        }
        startAutoSave();
      }
    }
  }, [
    isRecording,
    initializeSpeechRecognition,
    requestMicrophoneStream,
    stopAutoSave,
    startAutoSave,
    showToastMessage,
    audioStream,
  ]);

  useImperativeHandle(
    ref,
    () => ({
      startRecording: async () => {
        if (isRecordingRef.current) return;
        await handleRecordingClick();
      },
      stopRecording: async () => {
        if (!isRecordingRef.current) return;
        await handleRecordingClick();
      },
      toggleRecording: () => handleRecordingClick(),
      isRecording: () => isRecordingRef.current,
    }),
    [handleRecordingClick]
  );

  // Wire Space key: hold to start, release to stop
  useEffect(() => {
    const canUseShortcut = !(
      isProcessingImprovement ||
      isLoading ||
      isProcessingQueue
    );

    const onKeyDown = (e: KeyboardEvent) => {
      const isSpace =
        e.code === "Space" || e.key === " " || e.key === "Spacebar";
      if (!isSpace) return;
      if (!canUseShortcut) return;
      if (isEditableTarget(e.target)) return;
      if (e.repeat) return;

      if (!isSpaceDownRef.current) {
        isSpaceDownRef.current = true;
        if (!isRecordingRef.current) {
          e.preventDefault();
          void handleRecordingClick();
        }
      }
    };

    const onKeyUp = (e: KeyboardEvent) => {
      const isSpace =
        e.code === "Space" || e.key === " " || e.key === "Spacebar";
      if (!isSpace) return;
      if (!canUseShortcut) return;
      if (isEditableTarget(e.target)) return;

      if (isSpaceDownRef.current) {
        isSpaceDownRef.current = false;
        if (isRecordingRef.current) {
          e.preventDefault();
          void handleRecordingClick();
        }
      }
    };

    window.addEventListener("keydown", onKeyDown, { capture: true });
    window.addEventListener("keyup", onKeyUp, { capture: true });
    return () => {
      window.removeEventListener("keydown", onKeyDown, {
        capture: true,
      } as any);
      window.removeEventListener("keyup", onKeyUp, { capture: true } as any);
    };
  }, [
    handleRecordingClick,
    isProcessingImprovement,
    isLoading,
    isProcessingQueue,
    isEditableTarget,
  ]);

  // const handleFileUpload = useCallback(
  //   (url: string) => {
  //     onAudioUpload(url);
  //   },
  //   [onAudioUpload]
  // );

  const buttonContent = isRecording ? (
    <>
      <Square fill="white" className="w-4 h-4" />
      <p className="text-sm">Stop</p>
    </>
  ) : isProcessingImprovement || isProcessingQueue ? (
    <>
      <LoaderCircle animate className="w-4 h-4" />
      <p className="text-sm">Transcribing...</p>
    </>
  ) : (
    <>
      <MicIcon className="w-4 h-4" />
      <p className="text-sm">Start Recording</p>
    </>
  );

  return (
    <div
      className={cn(
        "transition-all duration-1000 ease-in-out relative h-[200px] flex flex-col items-center",
        className
      )}
    >
      <div className="inset-0 pointer-events-none z-[1] absolute top-[-89%]">
        <AudioVisualizer
          isActive={isRecording}
          audioStream={audioStream ?? undefined}
        />
      </div>
      <Button
        size="mini"
        onClick={handleRecordingClick}
        variant={isRecording ? "destructive" : "default"}
        disabled={
          !isRecording &&
          (isProcessingImprovement || isLoading || isProcessingQueue)
        }
        className={
          "w-fit-content text-[14px]  h-[48px] px-[24px]  rounded-[16px] flex items-center justify-center transition-all z-10 duration-1000 ease-in-out"
        }
        style={{
          outline: "none !important",
          border: "none !important",
          outlineOffset: 0,
        }}
        aria-label={
          isProcessingImprovement
            ? "Transcribing..."
            : isRecording
              ? "Stop recording"
              : "Start recording"
        }
      >
        <div
          className={cn(
            "flex gap-2 items-center transition-opacity duration-500 "
          )}
        >
          {buttonContent}
        </div>
      </Button>
      <img
        src={isRecording ? spaceInfoStop : spaceInfo}
        className="mt-[17px] mb-[35px] md:flex hidden relative z-10"
        alt="Space info"
      />
      {/* {!isRecording && (
        <div className="text-sm text-[#94A3B8] md:flex hidden items-center gap-2">
          <CloudUpload />

          <UploadButton accept="audio/*" onAudioUpload={handleFileUpload}>
            <p>
              You can also drag & drop audio file or{" "}
              <span className="text-blue-500 hover:opacity-60 cursor-pointer transition">
                choose
              </span>
            </p>
          </UploadButton>
        </div>
      )} */}
    </div>
  );
});
